import { Component, Input, OnInit } from '@angular/core';
import { AppComponentName, ExportData, ExportDataCriteria, ExportSaveAs, InterpolationInterval, InterpolationMethod, Message, MessageActionType, MessageType, Sensor, SensorReadingType, SensorReadingWithGPS, TimeZoneDisplay } from '@models';
import { SensorReadingInfo } from '@models/EventLog/SensorReadingInfo.model';
import { SensorStats } from '@models/Sensor/sensorStats.model';
import { AppMessageService } from '@services/message.service';
import { SensorService } from '@services/sensor.service';
import { UserService } from '@services/user.service';
import { CommonShared } from 'app/shared/classes/common';
import moment from 'moment';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';

interface DateRange {
  startDate: moment.Moment;
  endDate: moment.Moment;
}

@Component({
  standalone: false,
  selector: 'stats-readings-modal',
  templateUrl: './stats-readings-modal.component.html',
  styleUrls: ['./stats-readings-modal.component.scss']
})

export class StatsReadingsModalComponent implements OnInit {

  @Input() sensors: Sensor[];
  @Input() sensorReadings: SensorReadingInfo[];
  @Input() sensorReadingTypes: SensorReadingType[];
  @Input() includeOrigValues: boolean;
  @Input() includeCalValues: boolean;
  
  common = new CommonShared();
  
  modalDragOn: boolean = false;
  exportOptionsModalRef: BsModalRef;
  username = 'unknown';

  sensorStats: SensorStats[][] = [];

  statsTableCols = [
    { field: 'name', header: 'Sensor', width: '20%' },
    { field: 'readingType', header: 'Reading Type', width: '9%' },
    { field: 'min', header: 'Min', width: '9%' },
    { field: 'max', header: 'Max', width: '9%' },
    { field: 'avg', header: 'Avg', width: '9%' },
    { field: 'med', header: 'Med', width: '9%' },
    { field: 'count', header: '#Readings', width: '9%' },
    { field: 'lastReadingValue', header: 'Last Value', width: '9%' },
    { field: 'lastReadingDate', header: 'Last Reading', width: '18%' }
  ];

  constructor(public bsModalRef: BsModalRef, private toastr: ToastrService, private modalService: BsModalService,
              public _sensorService: SensorService, public _userService: UserService, public _appMessageService: AppMessageService) {
  }


  ngOnInit() {
    this._userService.getCurrentUserEmailUsingAzureAD().subscribe(
      results => {
        this.username = results['value'];
        if (this.username != null && this.username !== '') {
          this.startUp();
        }
      },
      err => {
        this.toastr.error('Error unable to read current user\'s info');
      });
  }


  startUp() {
  
  }


  setDragOn(drag: boolean) {
    this.modalDragOn = drag;

    if (drag) {
      setTimeout(() => {
        this.modalDragOn = false;
      },3000);
    }
  }


  ngOnChanges() {
    this.buildSensorStats();
  }


  buildSensorStats() {
    console.time('buildSensorStats');

    const significantDigits = 9;
    this.sensorStats = [];

    // Step 1: Pre-index readings by sensorId and typeId (O(r))
    const readingsIndex = new Map<string, SensorReadingInfo[]>();

    this.sensorReadings.forEach(reading => {
      const key = `${reading.sensorID}_${reading.typeID}`;
      if (!readingsIndex.has(key)) {
        readingsIndex.set(key, []);
      }
      readingsIndex.get(key).push(reading);
    });

    // Step 2: Process each reading type (O(t × s) - lookups are O(1))
    for (let index = 0; index < this.sensorReadingTypes.length; index++) {
      const readingType = this.sensorReadingTypes[index];
      const stats: SensorStats[] = [];

      let overallOrigValues: number[] = [];
      let overallOrigCount = 0;
      let overallCalValues: number[] = [];
      let overallCalCount = 0;

      this.sensors.forEach(sensor => {
        const key = `${sensor.id}_${readingType.id}`;
        const readings = readingsIndex.get(key) || [];

        if (readings.length === 0) return;

        // Pre-calculate common values once
        const origValues = this.includeOrigValues ? readings.map(r => Number(r.origValue)) : null;
        const calValues = this.includeCalValues ? readings.map(r => Number(r.value)) : null;
        const lastReading = readings[0]; // Assumes sorted by date descending

        // Process original values
        if (this.includeOrigValues && origValues) {
          const origStats = this.calculateStats(origValues, significantDigits);

          stats.push({
            sensorUid: sensor.uuid,
            sensorId: sensor.id,
            sensorName: this.common.getSensorDisplayName(sensor) + ' (o)',
            readingType: readingType.type,
            min: origStats.min,
            max: origStats.max,
            avg: origStats.avg,
            med: origStats.med,
            count: readings.length.toString(),
            lastReadingValue: Number(lastReading.origValue).toPrecision(significantDigits),
            lastReadingDate: lastReading.createdDate,
            lastReadingDisplayColor: this.common.getColorCodeForLastReadingDate(lastReading.createdDate)
          });

          // Accumulate for overall stats
          overallOrigValues.push(Number(origStats.min), Number(origStats.max));
          overallOrigCount += readings.length;
        }

        // Process calibrated values
        if (this.includeCalValues && calValues) {
          const calStats = this.calculateStats(calValues, significantDigits);

          stats.push({
            sensorUid: sensor.uuid,
            sensorId: sensor.id,
            sensorName: this.common.getSensorDisplayName(sensor) + ' (c)',
            readingType: readingType.type,
            min: calStats.min,
            max: calStats.max,
            avg: calStats.avg,
            med: calStats.med,
            count: readings.length.toString(),
            lastReadingValue: Number(lastReading.value).toPrecision(significantDigits),
            lastReadingDate: lastReading.createdDate,
            lastReadingDisplayColor: this.common.getColorCodeForLastReadingDate(lastReading.createdDate)
          });

          // Accumulate for overall stats
          overallCalValues.push(Number(calStats.min), Number(calStats.max));
          overallCalCount += readings.length;
        }
      });

      // Add overall stats rows
      if (this.includeOrigValues && overallOrigValues.length > 0) {
        const origStats = stats.filter(s => s.sensorName.endsWith('(o)'));
        const avgOfAvgs = this.calculateAverage(origStats.map(x => Number(x.avg)));
        const medOfMeds = this.calculateMedianNonDestructive(origStats.map(x => Number(x.med)));

        stats.push({
          sensorUid: 'Overall (o)',
          sensorId: 0,
          sensorName: 'OVERALL (o)',
          readingType: '',
          min: Math.min(...overallOrigValues).toPrecision(significantDigits),
          max: Math.max(...overallOrigValues).toPrecision(significantDigits),
          avg: avgOfAvgs.toPrecision(significantDigits),
          med: medOfMeds.toPrecision(significantDigits),
          count: overallOrigCount.toString(),
          lastReadingValue: '',
          lastReadingDate: undefined,
          lastReadingDisplayColor: ''
        });
      }

      if (this.includeCalValues && overallCalValues.length > 0) {
        const calStats = stats.filter(s => s.sensorName.endsWith('(c)'));
        const avgOfAvgs = this.calculateAverage(calStats.map(x => Number(x.avg)));
        const medOfMeds = this.calculateMedianNonDestructive(calStats.map(x => Number(x.med)));

        stats.push({
          sensorUid: 'Overall (c)',
          sensorId: 0,
          sensorName: 'OVERALL (c)',
          readingType: '',
          min: Math.min(...overallCalValues).toPrecision(significantDigits),
          max: Math.max(...overallCalValues).toPrecision(significantDigits),
          avg: avgOfAvgs.toPrecision(significantDigits),
          med: medOfMeds.toPrecision(significantDigits),
          count: overallCalCount.toString(),
          lastReadingValue: '',
          lastReadingDate: undefined,
          lastReadingDisplayColor: ''
        });
      }

      this.sensorStats[index] = stats;
    }

    console.timeEnd('buildSensorStats');
    console.log(`Processed ${this.sensorReadings.length} readings for ${this.sensorReadingTypes.length} types`);
  }


  calculateAverage(values: number[]): number {
    if (values.length === 0) {
      return 0; // Handle empty array case
    }
    let sum = 0;
    for (let i = 0; i < values.length; i++) {
      sum += values[i];
    }
    return sum / values.length;
  }


  calculateMedian(values: number[]): number {
    if (values.length === 0) {
        return undefined; // Or handle as an error
    }

    // Create a copy before sorting to avoid mutating the original array
    const sortedArray = [...values].sort((a, b) => a - b);
    const length = sortedArray.length;

    if (length % 2 === 1) {
        // Odd length
        return sortedArray[Math.floor(length / 2)];
    } else {
        // Even length
        const mid1 = sortedArray[length / 2 - 1];
        const mid2 = sortedArray[length / 2];
        return (mid1 + mid2) / 2;
    }
  }


  private calculateStats(values: number[], precision: number): {
    min: string;
    max: string;
    avg: string;
    med: string;
  } {
    if (values.length === 0) {
      return { min: '0', max: '0', avg: '0', med: '0' };
    }

    // Calculate min/max/sum in a single pass (avoids stack overflow from spread operator)
    let min = values[0];
    let max = values[0];
    let sum = values[0];

    for (let i = 1; i < values.length; i++) {
      const val = values[i];
      if (val < min) min = val;
      if (val > max) max = val;
      sum += val;
    }

    const avg = sum / values.length;
    const med = this.calculateMedianNonDestructive(values);

    return {
      min: min.toPrecision(precision),
      max: max.toPrecision(precision),
      avg: avg.toPrecision(precision),
      med: med.toPrecision(precision)
    };
  }


  private calculateMedianNonDestructive(values: number[]): number {
    if (values.length === 0) {
      return 0;
    }

    // Create a copy before sorting to avoid mutating the original array
    const sortedArray = [...values].sort((a, b) => a - b);
    const length = sortedArray.length;

    if (length % 2 === 1) {
      return sortedArray[Math.floor(length / 2)];
    } else {
      const mid1 = sortedArray[length / 2 - 1];
      const mid2 = sortedArray[length / 2];
      return (mid1 + mid2) / 2;
    }
  }


  exportStatsToCsv(index: number) {
    const rows = this.sensorStats && this.sensorStats[index] ? this.sensorStats[index] : [];
    if (rows.length === 0) {
      this.toastr.info('No stats available to export.');
      return;
    }

    const header = ['Sensor', 'Reading Type', 'Min', 'Max', 'Avg', 'Med', '#Readings', 'Last Value', 'Last Reading'];
    const csvLines: string[] = [header.map(value => this.escapeCsv(value)).join(',')];

    rows.forEach(row => {
      csvLines.push([
        row.sensorName,
        row.readingType,
        row.min,
        row.max,
        row.avg,
        row.med,
        row.count,
        row.lastReadingValue,
        row.lastReadingDate ? moment(row.lastReadingDate).format('YYYY-MM-DD HH:mm:ss') : ''
      ].map(value => this.escapeCsv(value)).join(','));
    });

    const readingType = this.sensorReadingTypes && this.sensorReadingTypes[index] ? this.sensorReadingTypes[index].type : 'stats';
    const safeReadingType = this.sanitizeFilePart(readingType);
    const fileName = `stats_${safeReadingType}_${moment().format('YYYY-MM-DD_HHmmss')}.csv`;

    const blob = new Blob([csvLines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }


  private escapeCsv(value: any): string {
    const stringValue = value === undefined || value === null ? '' : String(value);
    return `"${stringValue.replace(/"/g, '""')}"`;
  }


  private sanitizeFilePart(value: string): string {
    if (!value) {
      return 'stats';
    }

    const sanitized = value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    return sanitized || 'stats';
  }


  


}
